import { useEffect, useRef } from 'react';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import getElementPosition from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/utils/getElementPosition';
import { isScrollAnchoringSupported } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/scrollAnchoring';
import { useHistory } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';
import { useQueryParamHelper } from '@shared/core/queryString';

type KeepPageScrollPositionProps = {
  pagesRef: PagesRefs[];
  container: HTMLDivElement | null;
  targetPage?: HTMLDivElement | null;
  /**
   * Whether `targetPage` is the first section. Compared by page id upstream, not by ref identity:
   * a section remount swaps the DOM node while the id stays put, and an identity check would then
   * silently start pinning the first page again.
   */
  isTargetFirstPage?: boolean;
};

export const useKeepPageScrollPosition = ({ pagesRef, container, targetPage, isTargetFirstPage }: KeepPageScrollPositionProps) => {
  const history = useHistory();
  // Admin preview (joy's own preview iframe) — not bare isInIframe(), which is also true for Carbon
  // embeds and masked/custom domains where this is a real guest visit. Same detection as
  // useScrollToPageWhenUrlChanges and MobileTopBarNav, `ctx` fallback included.
  const { getValueString } = useQueryParamHelper();
  const isAdminPreview = isInIframe() && (getValueString('preview') === 'true' || getValueString('ctx') === 'adminGuestSitePreview');

  // Keep pagesRef in a ref so the observer callback always reads the latest
  // array without the effect needing to re-run (and destroy the observer).
  const pagesRefRef = useRef(pagesRef);
  pagesRefRef.current = pagesRef;

  // Track page heights across observer callbacks in a ref that persists
  // across effect re-runs so height deltas are never lost.
  const heightMapRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!pagesRef?.length || !targetPage || !container) {
      return;
    }

    // Safari-only fallback. Where scroll anchoring exists it compensates inside the layout
    // pass itself; this hook must measure growth first, and that measurement forces the
    // layout in which Chrome records the shift — so running it there adds CLS without
    // adding correctness. Candidacy is deterministic (target-only), so anchoring cannot
    // pin the wrong node. See scrollAnchoring.ts. PRNT-4799.
    if (isScrollAnchoringSupported()) {
      return;
    }

    // First page means the landing was the document TOP, not that section's current position — see
    // readDestinationY, which returns 0 for it. Pinning the section instead holds it at whatever
    // viewport offset it happened to have, so growth in the header ABOVE it gets compensated by
    // scrolling down, dragging the viewport off the top of the page. Traced on the designer preview
    // (root URL, Safari): the welcome section's document-Y went 144 -> 1177 as the header rendered
    // and this hook wrote scrollTop 0 -> 1033 to match, landing past the standalone website header.
    // Nothing sits above the first section to protect, so there is nothing to pin — same reasoning
    // as isSectionDeferred's root-route carve-out. PRNT-4799.
    //
    // Limited to the admin preview because that is where the contradiction actually bites. The
    // guest site was checked and lands correctly: SSR renders the header at its real height, so
    // almost nothing above the first section grows after mount and the pin has nothing to
    // compensate. `nossr` is what turns the same code path into a 1,000px drag in the preview.
    if (isTargetFirstPage && isAdminPreview) {
      return;
    }

    let observer: MutationObserver;

    const keepPageScrollPositionWhenDOMChanges = () => {
      // Track absolute-Y and scrollTop across callbacks so the correction
      // formula only compensates for *uncorrected* content growth.
      //
      // absoluteY = viewportTop + scrollTop is invariant under scroll — it
      // only changes when DOM content above the target grows or shrinks.
      // scrollTop changes from user scroll, ResizeObserver corrections, OR
      // our own prior adjustments.
      //
      // correction = contentShift − scrollChange gives the growth that hasn't
      // been compensated yet — so if another mechanism (the ResizeObserver in
      // useScrollToPageWhenUrlChanges) already scrolled to account for the
      // growth, scrollChange absorbs it and correction ≈ 0.
      let lastAbsoluteY = (targetPage?.getBoundingClientRect().top ?? 0) + (container?.scrollTop ?? 0);
      let lastScrollTop = container?.scrollTop ?? 0;

      // Seed the height map for any pages we haven't seen yet
      for (const { page, ref } of pagesRefRef.current) {
        if (ref && !heightMapRef.current.has(page.id)) {
          heightMapRef.current.set(page.id, ref.offsetHeight);
        }
      }

      const updatePageScroll = () => {
        const currentPages = pagesRefRef.current;

        // --- Compute how much correction is still needed ---
        const currentAbsoluteY = (targetPage?.getBoundingClientRect().top ?? 0) + (container?.scrollTop ?? 0);
        const currentScrollTop = container?.scrollTop ?? 0;

        const contentShift = currentAbsoluteY - lastAbsoluteY;
        const scrollChange = currentScrollTop - lastScrollTop;
        const correction = contentShift - scrollChange;

        // --- Walk pages to update height map and check relevance ---
        let growthAbove = 0;

        currentPages.forEach(({ ref: pageRef, page }) => {
          if (!pageRef) return;

          const trackedHeight = heightMapRef.current.get(page.id) ?? pageRef.offsetHeight;
          const newPageHeight = pageRef.offsetHeight;
          const heightDifference = newPageHeight - trackedHeight;

          // Always update tracked height
          heightMapRef.current.set(page.id, newPageHeight);

          if (heightDifference <= 0) return;

          const minContainerPositionY = container.scrollTop;
          const pagePosition = getElementPosition(pageRef, minContainerPositionY);
          const isPageBeforeVisibleArea = pagePosition.max <= minContainerPositionY;
          const isPagePartiallyVisibleArea = pagePosition.min <= minContainerPositionY && pagePosition.max >= minContainerPositionY;

          // Update the scroll position if the changed page is before the visible screen
          if (isPageBeforeVisibleArea || isPagePartiallyVisibleArea) {
            growthAbove += heightDifference;
          }
        });

        // Only adjust if there's uncorrected growth above the viewport.
        // A threshold of 1px avoids sub-pixel noise.
        //
        // Clamp to the growth actually observed: `scrollChange` can't distinguish user
        // scroll from compensating scroll, and user scroll accumulated between callbacks
        // would otherwise inflate the correction into a yank.
        const boundedCorrection = Math.min(correction, growthAbove);
        if (growthAbove > 0 && boundedCorrection > 1) {
          container.scrollTop = currentScrollTop + boundedCorrection;
        }

        // Re-anchor for the next callback.
        lastAbsoluteY = (targetPage?.getBoundingClientRect().top ?? 0) + (container?.scrollTop ?? 0);
        lastScrollTop = container?.scrollTop ?? 0;
      };

      // Listen for container's DOM changes to update the page scroll position base on pages height changes.
      // childList-only: content growth that needs compensation comes from nodes mounting (widget
      // data arriving, lazy chunks rendering). Observing attributes fired this on every React
      // class/style commit — a forced layout per commit across all page sections — and the
      // accommodations note More/Less toggle (class-driven, no childList change) now simply never
      // triggers the observer, which preserves the old skip behavior for it.
      //
      // SYNCHRONOUS, never debounced/deferred: MutationObserver callbacks run at the microtask
      // checkpoint, BEFORE the frame that contains the mutation paints — so a scrollTop
      // compensation applied here is invisible and costs no layout shift (same pre-paint slot
      // native scroll anchoring uses). The previous debounce(20, maxWait 40) pushed the
      // correction into a later task, after the displaced frame had painted — every correction
      // was a counted layout shift (measured 1.13 CLS on a mobile Aloha deep link, the
      // corrections chasing scrollHeight 7k → 26k). The measurement cost this rehabilitates is
      // one offsetHeight read per section per mutation batch, only while widgets mount.
      // PRNT-4799.
      observer = new MutationObserver(updatePageScroll);
      observer.observe(container, { childList: true, subtree: true });
    };

    keepPageScrollPositionWhenDOMChanges();

    return () => {
      observer.disconnect();
    };
    // container and targetPage control observer lifecycle.
    // pagesRef is read via ref inside the callback — not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, targetPage, isTargetFirstPage, isAdminPreview, history.location.pathname]);
};
